import { interpolate } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type DBHTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { useIsRTL } from "@/hooks";

import { Input2, Slider } from "shared/components";
import { MeasurementEstimationAccordion } from "./MeasurementEstimationAccordion";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface DBHFieldProps {
  hideMeasurementEstimationAccordion?: boolean;
  isMultiStem?: boolean | undefined | null;
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function DBHField<
  T extends DBHTreePropertyDescriptor = DBHTreePropertyDescriptor,
>({
  hideMeasurementEstimationAccordion,
  isMultiStem,
  onChange,
  treeDescriptor,
}: DBHFieldProps) {
  const isRTL = useIsRTL();

  const { isValid, messages } = useTreeDialogValidator<T>({
    checker: (property) => !!property?.value,
    properties: treeDescriptor.properties,
    treePropertyType: TreePropertyType.dbh,
  });

  const dbh = treeDescriptor.properties.get(TreePropertyType.dbh) as
    | T
    | undefined;

  const handleChange = () => (updater: (draft: T) => void) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.dbh,
      updater,
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel isValid={isValid}>
        {isMultiStem
          ? interpolate(
              "Addition of all tree stem diameters at {{distanceAboveGround}} above ground level",
              { distanceAboveGround: dbh?.distanceAboveGround },
            )
          : interpolate(
              "Tree diameter at {{distanceAboveGround}} above ground level",
              { distanceAboveGround: dbh?.distanceAboveGround },
            )}
      </FieldLabel>

      <div className="flex items-center gap-2">
        <Slider
          defaultValue={[0.1]}
          max={1.5}
          min={0.05}
          step={0.05}
          onValueChange={(value) => {
            handleChange()((draft) => {
              draft.value = parseFloat(value[0].toString())
                .toFixed(2)
                .toString();
            });
          }}
          value={[dbh?.value ? parseFloat(dbh.value) : 0]}
        />

        <Input2
          className="text-bodymd placeholder:text-bodymd w-15 min-w-15 not-rtl:pl-1 not-rtl:pr-0 relative h-5 pr-0 rtl:pl-0 rtl:pr-1"
          {...(isRTL
            ? {
                startClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute left-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-l-sm text-bodymd",
                startContent: "m",
              }
            : {
                endClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute right-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-r-sm text-bodymd",
                endContent: "m",
              })}
          max={1.0}
          min={0.1}
          step={0.05}
          onChange={(event) => {
            handleChange()((draft) => {
              draft.value = event.target.value;
            });
          }}
          rounded="sm"
          type="number"
          value={dbh?.value || ""}
        />
      </div>

      <FieldValidationMessages messages={messages} />

      {!hideMeasurementEstimationAccordion && (
        <MeasurementEstimationAccordion
          onClickSmall={() => {
            handleChange()((draft) => {
              draft.value = isMultiStem ? "0.30" : "0.20";
            });
          }}
          onClickMedium={() => {
            handleChange()((draft) => {
              draft.value = isMultiStem ? "0.50" : "0.40";
            });
          }}
          onClickLarge={() => {
            handleChange()((draft) => {
              draft.value = isMultiStem ? "0.80" : "0.70";
            });
          }}
        />
      )}
    </div>
  );
}
